import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import type { ProfileCompletionBreakdownRow } from '../Preview/api';
import type { CandidateAnalytics } from '../../types/candidateAnalytics';
import {
  PROFILE_SECTION_SHORT_LABELS,
  profileCompletionMessage,
  TARGET_SCREENING_PROMPT_COUNT,
} from '../../utils/sessionProgress';
import type {
  ProfileCompletionBreakdownItem,
  ProfileCompletionSection,
} from '../../utils/profileCompletionScore';
import './ScreeningReadinessModal.css';

type Props = {
  show: boolean;
  onHide: () => void;
  readinessPercent: number;
  readinessLabel: string;
  sections: ProfileCompletionBreakdownRow[];
  suggestions: string[];
  promptCount?: number;
};

function CheckIcon({ done }: { done: boolean }) {
  return (
    <span
      className={`ss-readiness-check ${done ? 'ss-readiness-check-done' : 'ss-readiness-check-pending'}`}
      aria-hidden
    >
      {done ? '✓' : '○'}
    </span>
  );
}

function sectionLabel(section: string, fallback: string): string {
  return PROFILE_SECTION_SHORT_LABELS[section as ProfileCompletionSection] ?? fallback;
}

export function ScreeningReadinessModal({
  show,
  onHide,
  readinessPercent,
  readinessLabel,
  sections,
  suggestions,
  promptCount = 0,
}: Props) {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<CandidateAnalytics | null>(null);

  useEffect(() => {
    if (!show) return;
    let mounted = true;
    (async () => {
      try {
        const res = await axiosInstance.get<{
          data?: CandidateAnalytics;
        }>(API_ROUTES.ANALYTICS.CANDIDATE_SNAPSHOT);
        const payload = res.data?.data ?? (res.data as CandidateAnalytics);
        if (mounted) setAnalytics(payload?.prompts ? payload : null);
      } catch {
        if (mounted) setAnalytics(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [show]);

  const incompleteSections = useMemo(
    () =>
      sections
        .filter((s) => s.earned < s.weight)
        .sort((a, b) => b.weight - b.earned - (a.weight - a.earned)),
    [sections]
  );

  const completedSections = useMemo(() => sections.filter((s) => s.earned >= s.weight), [sections]);

  const weakPrompts = useMemo(() => {
    if (!analytics?.prompts?.length) return [];
    return analytics.prompts
      .filter((p) => p.completeness_percent < 100)
      .sort((a, b) => a.completeness_percent - b.completeness_percent)
      .slice(0, 4);
  }, [analytics?.prompts]);

  const promptSlotsRemaining = Math.max(0, TARGET_SCREENING_PROMPT_COUNT - promptCount);
  const remainingPercent = Math.max(0, 100 - readinessPercent);
  const summaryMessage = profileCompletionMessage({
    percent: readinessPercent,
    breakdown: sections as ProfileCompletionBreakdownItem[],
    suggestions,
    isFullyComplete: readinessPercent >= 100,
  });

  const showStillToComplete =
    remainingPercent > 0 &&
    (incompleteSections.length > 0 || weakPrompts.length > 0 || suggestions.length > 0);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="ss-readiness-modal">
      <Modal.Header closeButton>
        <Modal.Title>Screening Readiness</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ss-readiness-summary mb-4">
          <div className="d-flex align-items-baseline gap-2 flex-wrap">
            <span className="display-6 fw-bold text-success mb-0">{readinessPercent}%</span>
            <span className="text-muted">{readinessLabel}</span>
          </div>
          <div
            className="progress mt-2"
            style={{ height: 8 }}
            role="progressbar"
            aria-valuenow={readinessPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Screening readiness"
          >
            <div className="progress-bar bg-success" style={{ width: `${readinessPercent}%` }} />
          </div>
          <p className="small text-muted mt-2 mb-0">
            {remainingPercent > 0 ? (
              <>
                About <strong>{remainingPercent}%</strong> left to reach a strong screening-ready
                profile. {summaryMessage}
                {promptSlotsRemaining > 0 ? (
                  <>
                    {' '}
                    Add <strong>{promptSlotsRemaining}</strong> more screening prompt
                    {promptSlotsRemaining === 1 ? '' : 's'} ({promptCount}/
                    {TARGET_SCREENING_PROMPT_COUNT} completed) to reach the full prompt score.
                  </>
                ) : null}
              </>
            ) : (
              <>Your weighted profile score is complete across all sections.</>
            )}
          </p>
        </div>

        {showStillToComplete ? (
          <section className="ss-readiness-block mb-4">
            <h6 className="ss-readiness-section-title">Still to complete</h6>
            <p className="small text-muted mb-3">
              These gaps explain the remaining {remainingPercent}% before you reach a fully
              screening-ready profile. Weights match your profile completion score (see breakdown
              below).
            </p>
            <ul className="ss-readiness-list mb-0">
              {incompleteSections.map((s) => (
                <li key={s.section} className="ss-readiness-list-item">
                  <CheckIcon done={false} />
                  <span>
                    Raise <strong>{sectionLabel(s.section, s.label)}</strong> (
                    {Math.round(s.sectionPercent)}% of this section — {s.earned}/{s.weight} pts
                    toward your {s.weight}% weight)
                  </span>
                </li>
              ))}
              {weakPrompts.map((p) => (
                <li key={p.id} className="ss-readiness-list-item">
                  <CheckIcon done={false} />
                  <span>
                    Strengthen prompt &ldquo;
                    {(p.title || 'Untitled').slice(0, 48)}
                    &rdquo; ({p.completeness_percent}% — {p.quality_label})
                  </span>
                </li>
              ))}
              {suggestions
                .filter(
                  (text) =>
                    !incompleteSections.some((s) =>
                      text.toLowerCase().includes(sectionLabel(s.section, s.label).toLowerCase())
                    )
                )
                .slice(0, 3)
                .map((text) => (
                  <li key={text} className="ss-readiness-list-item">
                    <CheckIcon done={false} />
                    <span>{text}</span>
                  </li>
                ))}
            </ul>
          </section>
        ) : null}

        {completedSections.length > 0 ? (
          <section className="ss-readiness-block">
            <h6 className="ss-readiness-section-title">Completed</h6>
            <ul className="ss-readiness-list mb-0">
              {completedSections.map((s) => (
                <li key={s.section} className="ss-readiness-list-item">
                  <CheckIcon done />
                  <span className="text-muted">
                    {sectionLabel(s.section, s.label)} ({s.weight}%)
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {sections.length > 0 ? (
          <section className="ss-readiness-block mt-4">
            <h6 className="ss-readiness-section-title">Score breakdown</h6>
            <p className="small text-muted mb-2">
              Each section contributes a fixed share of your 100% profile score.
            </p>
            <div className="row g-2 mt-1">
              {sections.map((s) => {
                const complete = s.earned >= s.weight;
                return (
                  <div key={s.section} className="col-6 col-md-4 col-lg-3">
                    <div
                      className={`ss-readiness-score-card rounded p-2 text-center h-100 ${complete ? 'ss-readiness-score-card--complete' : 'ss-readiness-score-card--in-progress'}`}
                    >
                      <div className="fw-semibold">
                        {s.earned}/{s.weight}
                      </div>
                      <div className="small text-muted">{sectionLabel(s.section, s.label)}</div>
                      <div className="small text-muted">
                        {Math.round(s.sectionPercent)}% of section
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" onClick={onHide}>
          Close
        </button>
        <button
          type="button"
          className="btn btn-success"
          onClick={() => {
            onHide();
            navigate('/analytics');
          }}
        >
          Full analytics
        </button>
      </Modal.Footer>
    </Modal>
  );
}
